import { useEffect, useMemo, useRef, useState } from 'react'
import type { MouseEvent as RMouseEvent, ReactNode } from 'react'
import { DEFAULT_MODEL, getModel, runTurn, setModel } from '../core/agent'
import type { ChatMessage, ToolTrace } from '../core/agent'
import type { LaneEvent } from '../core/tools'
import { useAuth } from '../core/auth'
import AuthDialog from './AuthDialog'
import AdminDialog from './AdminDialog'
import PasswordDialog from './PasswordDialog'
import { useStore } from '../core/store'
import { rangeToA1 } from '../core/a1'
import { parseFile } from '../core/io'
import type { ChatAttachment } from '../core/agent'

interface Bubble {
  role: 'user' | 'ai' | 'err'
  text: string
  attachment?: { kind: ChatAttachment['kind']; src?: string; name: string }
  traces?: ToolTrace[]
  lanes?: LaneEvent[]
}

interface ModelInfo {
  id: string
  name: string
  context: number | null
  inPrice: number | null
  outPrice: number | null
  inputModalities?: string[]
}

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp'])
const MAX_IMAGE_FILE = 8 * 1024 * 1024
const MAX_IMAGE_PAYLOAD = 2.5 * 1024 * 1024
const MAX_PDF_FILE = 4 * 1024 * 1024
const MAX_SHEET_FILE = 12 * 1024 * 1024
const SHEET_EXT_RE = /\.(xlsx|xlsm|xlsb|xls|csv|tsv|ods)$/i

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('손상되었거나 지원하지 않는 이미지입니다.'))
    image.src = src
  })
}

function readFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('이미지를 읽지 못했습니다.'))
    reader.readAsDataURL(file)
  })
}

function dataUrlBytes(dataUrl: string) {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1)
  return Math.floor((base64.length * 3) / 4)
}

async function prepareImage(file: File): Promise<ChatAttachment> {
  if (!IMAGE_TYPES.has(file.type)) throw new Error('PNG, JPG, WebP 이미지만 첨부할 수 있습니다.')
  if (file.size > MAX_IMAGE_FILE) throw new Error('원본 이미지는 8MB까지 첨부할 수 있습니다.')
  const source = await readFile(file)
  const image = await loadImage(source)
  const scale = Math.min(1, 1600 / image.naturalWidth, 1600 / image.naturalHeight)
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('이미지를 처리할 수 없습니다.')
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height)

  let dataUrl = ''
  for (const quality of [0.86, 0.72, 0.58]) {
    dataUrl = canvas.toDataURL('image/webp', quality)
    if (dataUrlBytes(dataUrl) <= MAX_IMAGE_PAYLOAD) break
  }
  if (dataUrlBytes(dataUrl) > MAX_IMAGE_PAYLOAD)
    throw new Error('이미지를 2.5MB 이하로 줄이지 못했습니다. 더 작은 이미지를 사용해 주세요.')
  return { kind: 'image', name: file.name, mime: 'image/webp', dataUrl, previewUrl: dataUrl }
}

function spreadsheetText(fileName: string, data: ArrayBuffer): string {
  const wb = parseFile(fileName, data)
  const sections: string[] = []
  let chars = 0
  const limit = 90_000
  for (const sheet of wb.sheets) {
    const lines = [`## ${sheet.name}`, '셀\t값/수식']
    for (const [address, cell] of Object.entries(sheet.cells)) {
      const value = cell.f ?? (cell.v === null || cell.v === undefined ? '' : String(cell.v))
      if (!value && !cell.s) continue
      const line = `${address}\t${value}`
      if (chars + line.length > limit) {
        lines.push('…(첨부 내용이 길어 이후 셀 생략)')
        break
      }
      lines.push(line)
      chars += line.length
    }
    sections.push(lines.join('\n'))
    if (chars >= limit) break
  }
  return sections.join('\n\n') || '(비어 있는 스프레드시트)'
}

async function prepareAttachment(file: File): Promise<ChatAttachment> {
  if (IMAGE_TYPES.has(file.type)) return prepareImage(file)
  if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
    if (file.size > MAX_PDF_FILE) throw new Error('PDF는 4MB까지 첨부할 수 있습니다.')
    return { kind: 'pdf', name: file.name, mime: 'application/pdf', dataUrl: await readFile(file) }
  }
  if (SHEET_EXT_RE.test(file.name)) {
    if (file.size > MAX_SHEET_FILE) throw new Error('Excel/CSV 파일은 12MB까지 첨부할 수 있습니다.')
    return {
      kind: 'spreadsheet',
      name: file.name,
      mime: file.type || 'application/octet-stream',
      text: spreadsheetText(file.name, await file.arrayBuffer()),
    }
  }
  throw new Error('이미지, PDF, Excel/CSV 파일만 첨부할 수 있습니다.')
}

const SUGGESTIONS = [
  '이 데이터 요약 통계 내줘',
  'B열 기준 내림차순 정렬',
  '헤더 행에 서식 넣고 열 너비 맞춰줘',
  '시트별로 병렬로 정리해줘',
  '합계 행 추가하고 수식으로 넣어줘',
]

export default function ChatPanel({
  controls,
  onHeaderMouseDown,
  onHeaderDoubleClick,
  bodyHidden,
}: {
  controls?: ReactNode
  onHeaderMouseDown?: (e: RMouseEvent) => void
  onHeaderDoubleClick?: () => void
  bodyHidden?: boolean
}) {
  const [bubbles, setBubbles] = useState<Bubble[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [live, setLive] = useState<ToolTrace[]>([])
  const [lanes, setLanes] = useState<LaneEvent[]>([])
  const [health, setHealth] = useState<{ ok: boolean } | null>(null)
  const [models, setModels] = useState<ModelInfo[]>([])
  const [recommended, setRecommended] = useState<string[]>([])
  const [model, setModelState] = useState<string>(() => getModel())
  const [pickerOpen, setPickerOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [pendingAttachment, setPendingAttachment] = useState<ChatAttachment | null>(null)
  const [authOpen, setAuthOpen] = useState(false)
  const [adminOpen, setAdminOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [authToast, setAuthToast] = useState('')

  const user = useAuth((s) => s.user)
  const refreshAuth = useAuth((s) => s.refresh)
  const logout = useAuth((s) => s.logout)

  const historyRef = useRef<ChatMessage[]>([])
  const logRef = useRef<HTMLDivElement>(null)
  const attachmentInputRef = useRef<HTMLInputElement>(null)

  const selRange = useStore((s) => s.selRange)
  const activeSheet = useStore((s) => s.activeSheet)

  useEffect(() => {
    void refreshAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetch('/api/health')
      .then((r) => r.json())
      .then((d) => setHealth({ ok: Boolean(d.hasKey) }))
      .catch(() => setHealth({ ok: false }))
    fetch('/api/models')
      .then((r) => r.json())
      .then((d) => {
        setModels(d.models ?? [])
        setRecommended(d.recommended ?? [])
        // 저장된 선택이 없으면 서버 기본값을 따른다
        if (!localStorage.getItem('aisheet.model') && d.defaultModel) {
          setModel(d.defaultModel)
          setModelState(d.defaultModel)
        }
      })
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' })
  }, [bubbles, live, lanes])

  const pick = (id: string) => {
    setModel(id)
    setModelState(id)
    setPickerOpen(false)
    setFilter('')
  }

  const attachFile = async (file: File) => {
    try {
      setPendingAttachment(await prepareAttachment(file))
    } catch (e: any) {
      setBubbles((b) => [...b, { role: 'err', text: e?.message ?? String(e) }])
    }
  }

  const send = async (text: string) => {
    const q = text.trim()
    if ((!q && !pendingAttachment) || busy) return
    if (!user) {
      setAuthOpen(true)
      return
    }
    const selected = models.find((m) => m.id === model)
    if (user.role !== 'admin' && user.credits < cashCost(selected)) {
      setBubbles((b) => [
        ...b,
        {
          role: 'err',
          text:
            user.credits <= 0
              ? '캐시가 없습니다. 관리자에게 캐시 지급을 요청하세요. (엑셀 기능은 계속 무료입니다)'
              : `캐시가 부족합니다. 이 모델은 호출당 캐시 ${cashCost(selected)}이 필요합니다. (보유: ${user.credits})`,
        },
      ])
      return
    }
    const attachment = pendingAttachment
    if (attachment?.kind === 'image' && selected?.inputModalities?.length && !selected.inputModalities.includes('image')) {
      setBubbles((b) => [...b, { role: 'err', text: '선택한 모델은 이미지 입력을 지원하지 않습니다. 비전 모델을 선택해 주세요.' }])
      return
    }
    const prompt = q || (attachment?.kind === 'pdf'
      ? '이 PDF 문서를 분석해 주세요.'
      : attachment?.kind === 'spreadsheet'
        ? '이 스프레드시트 파일을 분석해 주세요.'
        : '이 이미지를 분석해 주세요.')
    setInput('')
    setPendingAttachment(null)
    setBubbles((b) => [
      ...b,
      {
        role: 'user',
        text: prompt,
        attachment: attachment
          ? { kind: attachment.kind, src: attachment.previewUrl, name: attachment.name }
          : undefined,
      },
    ])
    setBusy(true)
    setLive([])
    setLanes([])
    const laneMap = new Map<number, LaneEvent>()

    try {
      const res = await runTurn(
        historyRef.current,
        prompt,
        {
          onTool: (t) => setLive((l) => [...l, t]),
          onLane: (e) => {
            laneMap.set(e.id, e)
            setLanes([...laneMap.values()].sort((a, b) => a.id - b.id))
          },
        },
        attachment ?? undefined
      )
      historyRef.current = res.history
      setBubbles((b) => [
        ...b,
        {
          role: 'ai',
          text: res.reply || '(완료)',
          traces: res.traces,
          lanes: laneMap.size ? [...laneMap.values()].sort((a, b) => a.id - b.id) : undefined,
        },
      ])
    } catch (e: any) {
      setBubbles((b) => [...b, { role: 'err', text: e?.message ?? String(e) }])
    } finally {
      setBusy(false)
      setLive([])
      setLanes([])
    }
  }

  const sheet = activeSheet()
  const selLabel = `${sheet.name}!${rangeToA1(selRange())}`
  const shortModel = model.split('/').pop() ?? model

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    const rec = models.filter((m) => recommended.includes(m.id))
    const rest = models.filter((m) => !recommended.includes(m.id))
    const match = (m: ModelInfo) => !q || m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q)
    return { rec: rec.filter(match), rest: rest.filter(match).slice(0, 200) }
  }, [models, recommended, filter])

  const price = (m?: ModelInfo) =>
    m?.inPrice != null && m?.outPrice != null ? `$${m.inPrice.toFixed(2)}/$${m.outPrice.toFixed(2)} per 1M` : ''

  // 서버 costForModel 과 같은 티어: 출력단가 ≥25→5, ≥10→3, ≥3→2, 그 외 1
  const cashCost = (m?: ModelInfo) => {
    const out = m?.outPrice
    if (out == null) return 1
    if (out >= 25) return 5
    if (out >= 10) return 3
    if (out >= 3) return 2
    return 1
  }

  return (
    <>
      <div className="chat-head" onMouseDown={onHeaderMouseDown} onDoubleClick={onHeaderDoubleClick}>
        <span className={`dot${health && !health.ok ? ' off' : ''}`} />
        AI 어시스턴트
        {controls}
      </div>

      {!bodyHidden && (
        <>
          <div className="model-bar" onMouseDown={(e) => e.stopPropagation()}>
            <button
              className="model-pick"
              onClick={() => setPickerOpen((v) => !v)}
              title={health?.ok === false ? 'API 키 없음' : model}
            >
              <span className="model-name">{health?.ok === false ? 'API 키 없음' : shortModel}</span>
              <span className="caret">▾</span>
            </button>
            {model === DEFAULT_MODEL && <span className="model-tag">기본</span>}

            <span className="auth-area">
              {user === null && (
                <button className="auth-btn" onClick={() => setAuthOpen(true)}>
                  로그인
                </button>
              )}
              {user && (
                <>
                  <button
                    className="auth-user"
                    title={`${user.username} (${user.role === 'admin' ? '관리자' : '사용자'}) — 비밀번호 변경`}
                    onClick={() => setPasswordOpen(true)}
                  >
                    {user.username}
                  </button>
                  <span className="auth-credits" title="AI 호출 1회당 캐시 1이 차감됩니다">
                    🪙 {user.role === 'admin' ? '∞' : user.credits}
                  </span>
                  {user.role === 'admin' && (
                    <button className="auth-btn" onClick={() => setAdminOpen(true)}>
                      캐시 관리
                    </button>
                  )}
                  <button className="auth-btn" onClick={() => void logout()}>
                    로그아웃
                  </button>
                </>
              )}
            </span>

            {pickerOpen && (
              <div className="model-pop">
                <input
                  className="model-filter"
                  autoFocus
                  placeholder="모델 검색 (예: gemini, sonnet, gpt)"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <div className="model-list">
                  {filtered.rec.length > 0 && <div className="model-group">추천</div>}
                  {filtered.rec.map((m) => (
                    <button key={m.id} className={`model-row${m.id === model ? ' on' : ''}`} onClick={() => pick(m.id)}>
                      <span className="mr-name">{m.name}</span>
                      <span className="mr-cash" title="호출당 차감 캐시">🪙{cashCost(m)}</span>
                      <span className="mr-meta">{price(m)}</span>
                    </button>
                  ))}
                  {filtered.rest.length > 0 && <div className="model-group">전체 ({models.length})</div>}
                  {filtered.rest.map((m) => (
                    <button key={m.id} className={`model-row${m.id === model ? ' on' : ''}`} onClick={() => pick(m.id)}>
                      <span className="mr-name">{m.id}</span>
                      <span className="mr-cash" title="호출당 차감 캐시">🪙{cashCost(m)}</span>
                      <span className="mr-meta">{price(m)}</span>
                    </button>
                  ))}
                  {!filtered.rec.length && !filtered.rest.length && <div className="model-group">일치하는 모델 없음</div>}
                </div>
              </div>
            )}
          </div>

          <div className="chat-log" ref={logRef}>
            {!bubbles.length && (
              <div className="chat-empty">
                시트를 말로 조작하세요. 지금 선택: <b>{selLabel}</b>
                <br />
                파일은 창 아무 데나 <b>드래그 앤 드롭</b> 하면 열립니다.
                {user === null && (
                  <div className="auth-note-inline">
                    엑셀 기능은 무료 · AI는 <button className="auth-link" onClick={() => setAuthOpen(true)}>로그인</button> 후
                    관리자에게 캐시를 받으면 사용할 수 있어요.
                  </div>
                )}
                {user && user.role !== 'admin' && user.credits <= 0 && (
                  <div className="auth-note-inline">보유 캐시가 0입니다. 관리자에게 캐시 지급을 요청하세요.</div>
                )}
                <div className="chip-row">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} className="chip" onClick={() => void send(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {bubbles.map((b, i) => (
              <div key={i} className="bubble-wrap">
                {b.lanes && <LaneList lanes={b.lanes} />}
                {b.traces && b.traces.length > 0 && (
                  <div className="trace">
                    {b.traces.map((t, j) => (
                      <div key={j} className={`trace-item${t.ok ? '' : ' fail'}`} title={JSON.stringify(t.args)}>
                        {t.ok ? '✓' : '✕'} {t.name} — {t.summary}
                      </div>
                    ))}
                  </div>
                )}
                <div className={`msg ${b.role}`}>
                  {b.attachment?.src && (
                    <img className="msg-image" src={b.attachment.src} alt={b.attachment.name} />
                  )}
                  {b.attachment && !b.attachment.src && (
                    <div className="msg-file">
                      {b.attachment.kind === 'pdf' ? '📄' : '📊'} {b.attachment.name}
                    </div>
                  )}
                  {b.text}
                </div>
              </div>
            ))}

            {busy && (
              <div className="bubble-wrap">
                {lanes.length > 0 && <LaneList lanes={lanes} />}
                <div className="trace">
                  {live.map((t, j) => (
                    <div key={j} className={`trace-item${t.ok ? '' : ' fail'}`}>
                      {t.ok ? '✓' : '✕'} {t.name} — {t.summary}
                    </div>
                  ))}
                </div>
                <div className="msg ai">
                  <span className="spinner" /> 작업 중…
                </div>
              </div>
            )}
          </div>

          <div className="chat-input-wrap" onMouseDown={(e) => e.stopPropagation()}>
            <input
              ref={attachmentInputRef}
              type="file"
              hidden
              accept="image/png,image/jpeg,image/webp,.pdf,.xlsx,.xlsm,.xlsb,.xls,.csv,.tsv,.ods"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void attachFile(file)
                e.target.value = ''
              }}
            />
            {pendingAttachment && (
              <div className="chat-attachment">
                {pendingAttachment.previewUrl ? (
                  <img src={pendingAttachment.previewUrl} alt={pendingAttachment.name} />
                ) : (
                  <div className="chat-file-icon">{pendingAttachment.kind === 'pdf' ? '📄' : '📊'}</div>
                )}
                <span title={pendingAttachment.name}>{pendingAttachment.name}</span>
                <button type="button" aria-label="첨부 파일 제거" onClick={() => setPendingAttachment(null)}>
                  ×
                </button>
              </div>
            )}
            <textarea
              className="chat-input"
              rows={2}
              value={input}
              placeholder={`무엇을 할까요?  (선택: ${selLabel})`}
              disabled={busy}
              onChange={(e) => setInput(e.target.value)}
              onPaste={(e) => {
                const file = [...e.clipboardData.items]
                  .find((item) => item.kind === 'file' && item.type.startsWith('image/'))
                  ?.getAsFile()
                if (file) {
                  e.preventDefault()
                  void attachFile(file)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void send(input)
                }
              }}
            />
            <div className="chat-send-row">
              <span className="chat-hint">Enter 전송 · Shift+Enter 줄바꿈</span>
              <button
                className="chat-attach"
                type="button"
                title="이미지, PDF, Excel 첨부"
                disabled={busy}
                onClick={() => attachmentInputRef.current?.click()}
              >
                ＋
              </button>
              <button
                className="chat-send"
                disabled={busy || (!input.trim() && !pendingAttachment)}
                onClick={() => void send(input)}
              >
                보내기
              </button>
            </div>
          </div>
        </>
      )}

      {authOpen && <AuthDialog onClose={() => setAuthOpen(false)} />}
      {adminOpen && <AdminDialog onClose={() => setAdminOpen(false)} />}
      {passwordOpen && (
        <PasswordDialog
          onClose={() => setPasswordOpen(false)}
          onDone={(m) => {
            setAuthToast(m)
            window.setTimeout(() => setAuthToast((cur) => (cur === m ? '' : cur)), 3200)
          }}
        />
      )}
      {authToast && <div className="toast">{authToast}</div>}
    </>
  )
}

function LaneList({ lanes }: { lanes: LaneEvent[] }) {
  const done = lanes.filter((l) => l.status !== 'running').length
  return (
    <div className="lanes">
      <div className="lanes-head">
        ⚡ 병렬 실행 {done}/{lanes.length}
      </div>
      {lanes.map((l) => (
        <div key={l.id} className={`lane ${l.status}`}>
          <span className="lane-icon">
            {l.status === 'running' ? <span className="spinner sm" /> : l.status === 'done' ? '✓' : '✕'}
          </span>
          <span className="lane-label">{l.label}</span>
          {l.detail && <span className="lane-detail">{l.detail}</span>}
        </div>
      ))}
    </div>
  )
}
